import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { FileWarning, CheckCircle2, XCircle, Loader2, Send } from "lucide-react";
import { toast } from "sonner";

const STATUS_BADGES = {
  pending: ["En attente", "bg-amber-100 text-amber-700"],
  approved: ["Approuvée", "bg-green-100 text-green-700"],
  rejected: ["Rejetée", "bg-red-100 text-red-700"],
};
const StatusBadge = ({ s }) => {
  const [label, cls] = STATUS_BADGES[s] || [s, "bg-slate-100 text-slate-500"];
  return <span className={`text-[11px] font-bold px-2 py-0.5 rounded-full ${cls}`}>{label}</span>;
};

const ANOMALY_COLORS = {
  non_pointee: "bg-red-100 text-red-700",
  en_retard: "bg-amber-100 text-amber-700",
  cours_non_termine: "bg-orange-100 text-orange-700",
};

// ---------- Vue enseignant ----------
const TeacherView = () => {
  const [anomalies, setAnomalies] = useState([]);
  const [mine, setMine] = useState([]);
  const [dlg, setDlg] = useState(null); // anomalie sélectionnée
  const [reason, setReason] = useState("");
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try {
      const [a, m] = await Promise.all([api.get("/corrections/my-anomalies"), api.get("/corrections/my")]);
      setAnomalies(a.data); setMine(m.data);
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
  };
  useEffect(() => { load(); }, []);

  const submit = async () => {
    if (!reason.trim()) return toast.error("Motif obligatoire");
    setSaving(true);
    try {
      await api.post("/corrections", { slot_id: dlg.slot_id, date: dlg.date, anomaly_type: dlg.anomaly_type, reason });
      toast.success("Contestation envoyée à l'administration");
      setDlg(null); setReason("");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
    finally { setSaving(false); }
  };

  return (
    <div className="space-y-6" data-testid="teacher-corrections-view">
      <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
        <div className="px-5 py-3 border-b border-slate-100 bg-slate-50 font-heading font-bold text-[#1E3A8A]">
          Anomalies détectées (14 derniers jours)
        </div>
        <div className="divide-y divide-slate-100">
          {anomalies.length === 0 && <div className="text-center text-slate-400 py-8 text-sm" data-testid="no-anomalies">Aucune anomalie contestable. Bravo pour votre ponctualité !</div>}
          {anomalies.map((a, i) => (
            <div key={i} className="px-5 py-3 flex items-center justify-between gap-3 flex-wrap" data-testid={`anomaly-${a.slot_id}-${a.date}`}>
              <div>
                <div className="flex items-center gap-2 flex-wrap">
                  <span className={`text-[11px] font-bold px-2 py-0.5 rounded-full ${ANOMALY_COLORS[a.anomaly_type]}`}>{a.label}</span>
                  <span className="font-semibold text-slate-800 text-sm">{a.subject} — {a.class_name}</span>
                </div>
                <div className="text-xs text-slate-500 mt-0.5">
                  {a.day} {a.date} · {a.start_time}–{a.end_time}
                  {a.retard_min != null && ` · retard ${a.retard_min} min`}
                  {a.validated_minutes != null && ` · ${Math.round(a.validated_minutes)} min validées`}
                </div>
              </div>
              <Button size="sm" onClick={() => { setDlg(a); setReason(""); }} className="bg-[#1E3A8A] text-white gap-1" data-testid={`contest-btn-${a.slot_id}-${a.date}`}>
                <FileWarning className="h-3.5 w-3.5" />Contester
              </Button>
            </div>
          ))}
        </div>
      </div>

      <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
        <div className="px-5 py-3 border-b border-slate-100 bg-slate-50 font-heading font-bold text-[#1E3A8A]">Mes contestations</div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-xs uppercase text-slate-500">
              <tr>
                <th className="px-4 py-2 text-left">Séance</th>
                <th className="px-4 py-2 text-left">Anomalie</th>
                <th className="px-4 py-2 text-left">Motif</th>
                <th className="px-4 py-2 text-left">Statut</th>
                <th className="px-4 py-2 text-left">Décision</th>
              </tr>
            </thead>
            <tbody>
              {mine.map(c => (
                <tr key={c.id} className="border-t border-slate-100" data-testid={`my-correction-${c.id}`}>
                  <td className="px-4 py-2"><span className="font-semibold">{c.subject}</span> {c.class_name}<div className="text-xs text-slate-400">{c.date} {c.start_time}</div></td>
                  <td className="px-4 py-2 text-xs">{c.label}</td>
                  <td className="px-4 py-2 text-xs text-slate-500 max-w-[200px] truncate">{c.reason}</td>
                  <td className="px-4 py-2"><StatusBadge s={c.status} /></td>
                  <td className="px-4 py-2 text-xs text-slate-500">{c.decision_note || (c.decided_by_name ? `Par ${c.decided_by_name}` : "—")}</td>
                </tr>
              ))}
              {mine.length === 0 && <tr><td colSpan={5} className="px-4 py-8 text-center text-slate-400">Aucune contestation envoyée.</td></tr>}
            </tbody>
          </table>
        </div>
      </div>

      <Dialog open={!!dlg} onOpenChange={(o) => !o && setDlg(null)}>
        <DialogContent data-testid="contest-dialog">
          <DialogHeader><DialogTitle>Contester : {dlg?.label}</DialogTitle></DialogHeader>
          {dlg && (
            <div className="space-y-3">
              <div className="text-sm bg-slate-50 rounded-lg p-3">
                <b>{dlg.subject}</b> — {dlg.class_name}<br />
                <span className="text-slate-500 text-xs">{dlg.day} {dlg.date} · {dlg.start_time}–{dlg.end_time}</span>
              </div>
              <div>
                <Label>Motif / justification *</Label>
                <Textarea rows={3} value={reason} onChange={e => setReason(e.target.value)}
                  placeholder="Ex : Le cours a bien eu lieu mais mon téléphone était déchargé..." data-testid="contest-reason" />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setDlg(null)}>Annuler</Button>
            <Button onClick={submit} disabled={saving} className="bg-[#1E3A8A] text-white gap-1" data-testid="contest-submit">
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Send className="h-4 w-4" />Envoyer</>}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// ---------- Vue administration ----------
const AdminView = () => {
  const { user } = useAuth();
  const canDecide = ["admin", "super_admin", "director"].includes(user?.role);
  const [rows, setRows] = useState([]);
  const [tab, setTab] = useState("pending");
  const [busy, setBusy] = useState("");
  const [noteDlg, setNoteDlg] = useState(null); // {correction, decision}
  const [note, setNote] = useState("");

  const load = async () => {
    try { const { data } = await api.get("/corrections"); setRows(data); }
    catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
  };
  useEffect(() => { load(); }, []);

  const decide = async () => {
    const { correction, decision } = noteDlg;
    setBusy(correction.id);
    try {
      await api.post(`/corrections/${correction.id}/decide`, { decision, note });
      toast.success(decision === "approved" ? "Contestation approuvée — séance régularisée" : "Contestation rejetée");
      setNoteDlg(null); setNote("");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
    finally { setBusy(""); }
  };

  const shown = rows.filter(r => tab === "pending" ? r.status === "pending" : r.status !== "pending");

  return (
    <div className="space-y-4" data-testid="admin-corrections-view">
      <Tabs value={tab} onValueChange={setTab}>
        <TabsList>
          <TabsTrigger value="pending" data-testid="tab-pending">En attente ({rows.filter(r => r.status === "pending").length})</TabsTrigger>
          <TabsTrigger value="history" data-testid="tab-history">Historique</TabsTrigger>
        </TabsList>
        <TabsContent value={tab} className="mt-4">
          <div className="bg-white rounded-2xl border border-slate-200 overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-xs uppercase text-slate-500">
                <tr>
                  <th className="px-4 py-2 text-left">Enseignant</th>
                  <th className="px-4 py-2 text-left">Séance</th>
                  <th className="px-4 py-2 text-left">Anomalie</th>
                  <th className="px-4 py-2 text-left">Motif</th>
                  <th className="px-4 py-2 text-left">Statut</th>
                  {canDecide && tab === "pending" && <th className="px-4 py-2" />}
                </tr>
              </thead>
              <tbody>
                {shown.map(c => (
                  <tr key={c.id} className="border-t border-slate-100" data-testid={`correction-${c.id}`}>
                    <td className="px-4 py-2 font-semibold">{c.teacher_name}</td>
                    <td className="px-4 py-2"><b>{c.subject}</b> {c.class_name}<div className="text-xs text-slate-400">{c.date} {c.start_time}–{c.end_time}</div></td>
                    <td className="px-4 py-2"><span className={`text-[11px] font-bold px-2 py-0.5 rounded-full ${ANOMALY_COLORS[c.anomaly_type]}`}>{c.label}</span></td>
                    <td className="px-4 py-2 text-xs text-slate-600 max-w-[220px]">{c.reason}</td>
                    <td className="px-4 py-2"><StatusBadge s={c.status} />{c.decided_by_name && <div className="text-[10px] text-slate-400 mt-0.5">{c.decided_by_name}</div>}</td>
                    {canDecide && tab === "pending" && (
                      <td className="px-4 py-2 text-right whitespace-nowrap">
                        <Button size="sm" onClick={() => { setNoteDlg({ correction: c, decision: "approved" }); setNote(""); }} disabled={busy === c.id}
                          className="bg-[#166534] text-white gap-1 mr-2" data-testid={`approve-${c.id}`}>
                          <CheckCircle2 className="h-3.5 w-3.5" />Approuver
                        </Button>
                        <Button size="sm" variant="outline" onClick={() => { setNoteDlg({ correction: c, decision: "rejected" }); setNote(""); }} disabled={busy === c.id}
                          className="text-red-600 border-red-200 gap-1" data-testid={`reject-${c.id}`}>
                          <XCircle className="h-3.5 w-3.5" />Rejeter
                        </Button>
                      </td>
                    )}
                  </tr>
                ))}
                {shown.length === 0 && <tr><td colSpan={6} className="px-4 py-8 text-center text-slate-400">{tab === "pending" ? "Aucune contestation en attente." : "Aucun historique."}</td></tr>}
              </tbody>
            </table>
          </div>
        </TabsContent>
      </Tabs>

      <Dialog open={!!noteDlg} onOpenChange={(o) => !o && setNoteDlg(null)}>
        <DialogContent data-testid="decision-dialog">
          <DialogHeader>
            <DialogTitle>{noteDlg?.decision === "approved" ? "Approuver et régulariser" : "Rejeter la contestation"}</DialogTitle>
          </DialogHeader>
          {noteDlg && (
            <div className="space-y-3">
              <div className="text-sm bg-slate-50 rounded-lg p-3">
                <b>{noteDlg.correction.teacher_name}</b> — {noteDlg.correction.label}<br />
                <span className="text-xs text-slate-500">{noteDlg.correction.subject} · {noteDlg.correction.class_name} · {noteDlg.correction.date}</span>
              </div>
              {noteDlg.decision === "approved" && (
                <p className="text-xs text-green-700 bg-green-50 rounded-lg p-2">
                  ✓ La séance sera automatiquement régularisée (heures validées pour la paie).
                </p>
              )}
              <div>
                <Label>Note (optionnel)</Label>
                <Textarea rows={2} value={note} onChange={e => setNote(e.target.value)} placeholder="Justification de la décision..." data-testid="decision-note" />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setNoteDlg(null)}>Annuler</Button>
            <Button onClick={decide} disabled={!!busy}
              className={noteDlg?.decision === "approved" ? "bg-[#166534] text-white" : "bg-red-600 text-white"} data-testid="decision-submit">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Confirmer"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default function CorrectionsPage() {
  const { user } = useAuth();
  const isTeacher = user?.role === "teacher";
  return (
    <div className="space-y-6" data-testid="corrections-page">
      <div>
        <div className="chip mb-2"><FileWarning className="h-3 w-3" /><span>ANOMALIES DE POINTAGE</span></div>
        <h1 className="font-heading font-extrabold text-3xl text-[#1E3A8A]">
          {isTeacher ? "Mes anomalies & contestations" : "Corrections de pointage"}
        </h1>
        <p className="text-slate-500 mt-1">
          {isTeacher
            ? "Contestez une anomalie détectée (séance non pointée, retard, cours non terminé). L'administration statuera."
            : "Validez ou rejetez les contestations des enseignants. Une approbation régularise automatiquement la séance."}
        </p>
      </div>
      {isTeacher ? <TeacherView /> : <AdminView />}
    </div>
  );
}
